import React, {useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Radio,
    RadioGroup,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {previewFsReport} from '@/services/auditService/auditFsPreviewDownloadService';
import {DigitAmountBoxes} from '@/pages/components/common/DigitAmountBoxes';
import {useCompanyAnnualStore} from '@/store/audit/companyAnnualStore';

interface TaxReturnPreviewPanelProps {
    companyId: number;
    annualId: number;
    yearOfAssessment?: string | null;
    active: boolean;
}

type PtrSheet = 'PTR1' | 'PTR2' | 'PTR3' | 'PTR4';

/** digitAmount / digitPair / basisPeriod72 / yesNoFollowUp: Part 7 composites; checkbox: S-form / Part 5. */
type FieldKind =
    | 'text'
    | 'amount'
    | 'yesNo'
    | 'digitAmount'
    | 'digitPair'
    | 'basisPeriod72'
    | 'yesNoFollowUp'
    | 'rbc7101'
    | 'checkbox';

type FieldDef = {
    /** Dot path into sheet VO, e.g. offshoreBizProfitDetail.internetRelatedProfits */
    path: string;
    /** Exact label text copied from PTR.xlsx */
    label: string;
    kind: FieldKind;
    /** Indent nested Excel sub-items */
    indent?: number;
    /** DigitAmountBoxes: only filled digit cells, no currency (e.g. HSIC). */
    digitFilledOnly?: boolean;
    /** DigitAmountBoxes: empty value still shows empty boxes. */
    digitBlankWhenEmpty?: boolean;
    /** Override value cell text-align (default by kind). */
    valueAlign?: 'left' | 'right';
    /** Keep description on one line (no wrap). */
    descNowrap?: boolean;
    /** yesNoFollowUp: VO path for line-2 inline value. */
    followUpPath?: string;
    /** yesNoFollowUp: line-2 label before bold value. */
    followUpLabel?: string;
    /** Extra "Label: value" fragments appended after description (e.g. 7.6 Currency / Conversion rate). */
    inlinePairs?: Array<{label: string; path: string}>;
};

type SectionDef = {
    title: string;
    /** Inline secondary text after title (e.g. Part 6 hint). */
    titleHint?: string;
    /** Optional Excel instruction lines under the part title */
    note?: string;
    /**
     * dualColCheckbox: Excel Part 9 - 9.1-9.11 left, 9.12-9.22 right,
     * each side = item no | description | checkbox.
     * dualColDigit: Part 12 - 12.1-12.12 left, 12.13-12.23 right,
     * each side = item no | description | DigitAmountBoxes; trailing text rows after.
     * part11: after 11.1-11.4, header row Commercial Building | Machinery or Plant,
     * then left 11.5-11.7 / right 11.8-11.11.
     * descValue4to6: description : value = 40% : 60% (PTR2 Part 4).
     * part6: first 2 fields 2:8; remaining rows default + optional nowrap desc.
     */
    layout?: 'default' | 'dualColCheckbox' | 'dualColDigit' | 'part11' | 'descValue4to6' | 'part6';
    fields: FieldDef[];
};

type TaxReturnPreviewPayload = {
    ptr1?: Record<string, unknown> | null;
    ptr2?: Record<string, unknown> | null;
    ptr3?: Record<string, unknown> | null;
    ptr4?: Record<string, unknown> | null;
};

const SHEETS: Array<{key: PtrSheet; label: string}> = [
    {key: 'PTR1', label: 'PTR1'},
    {key: 'PTR2', label: 'PTR2'},
    {key: 'PTR3', label: 'PTR3'},
    {key: 'PTR4', label: 'PTR4'},
];

/** PTR1  -  labels from PTR.xlsx */
const PTR1_SECTIONS: SectionDef[] = [
    {
        title: 'PART 1     STATEMENT OF ASSESSABLE PROFITS OR ADJUSTED LOSS',
        fields: [
        {path: 'assessableProfitsBeforeLoss', label: '1.1 Assessable Profits (before loss brought forward) If NIL, enter "0"', kind: 'digitAmount'},
        {path: 'adjustedLossBeforeLoss', label: '1.2 Adjusted Loss (before loss brought forward)   If NIL, enter "0"', kind: 'digitAmount'},
        {path: 'lossBroughtForward', label: '1.3 Loss brought forward from prior year       If NIL, enter "0"', kind: 'digitAmount'},
        ],
    },
    {
        title: 'PART 2     TAX LIABILITY OR REPAYMENT',
        fields: [
        {path: 'taxPayable', label: '2.1 Tax payable If NIL, enter "0"', kind: 'digitAmount'},
        {path: 'taxRepayable', label: '2.2 Tax Repayable  If NIL, enter "0"', kind: 'digitAmount'},
        {path: 'twoTierTaxThisYear', label: '2.3 Are you chargeable at two-tiered rates for this year of assessment? (For a corporation with connected entities, no other connected entity elects to be chargeable at two-tiered rates.)', kind: 'yesNo'},
        ],
    },
    {
        title: 'PART 3     SPECIFIED TRANSACTIONS AND MATTERS',
        fields: [
        {path: 'ispayIntellectualPropertyFee', label: '3.1 During the basis period, did you pay or accrue to a non-resident person any sum for the use/assignment of intellectual property specified in section 15(1)(a), (b), (ba) or (bb) of the Inland Revenue Ordinance? If yes, submit details of the sum as stated in the Notes and include the sum in Item 12.14.', kind: 'yesNo'},
        {path: 'hasdeemedAssessableProfits', label: '3.2 Did you have any deemed assessable profits under section 20AE, 20AF, 20AX and/or 20AY of the Inland Revenue Ordinance or section 22 and/or 23 of Schedule 16E to the Inland Revenue Ordinance for this year of assessment? If yes, submit the information as required in the Notes.', kind: 'yesNo'},
        {path: 'hasshortMediumTermDebtInstruments', label: '3.3 Does the amount of the Assessable Profits/Adjusted Loss entered in Part 1 include any interest, profits/loss arising from "short/medium term debt instruments" (issued before 1 April 2018)? If yes, submit the information as required in the Notes.', kind: 'yesNo'},
        {path: 'doubleTaxationRelief', label: '3.4 Do you claim tax relief for this year of assessment pursuant to an arrangement for avoidance of double taxation specified under section 49(1) or 49(1A) of the Inland Revenue Ordinance? If yes, submit the information as required in the Notes.', kind: 'yesNo'},
        {path: 'advanceRuling', label: '3.5 Have you obtained an advance ruling relating to this year of assessment? If yes, submit the information as required in the Notes.', kind: 'yesNo'},
        {path: 'debtTreatment', label: '3.6 Do you claim debt treatment for an arrangement for this year of assessment as "an originator" or "a bond-issuer" of a specified alternative bond scheme under section 40AB of and Schedule 17A to the Inland Revenue Ordinance?', kind: 'yesNo'},
        {path: 'regulatoryCapitalDeduction', label: '3.7 Do you claim deduction for distribution arising from a regulatory capital security for this year of assessment?', kind: 'yesNo'},
        {path: 'permanentEstablishment', label: '3.8 Were you a permanent establishment in Hong Kong of a non-Hong Kong resident person for this year of assessment? If yes, submit the information as required under Item (3)(t) in Part 1 of Note G.', kind: 'yesNo'},
        {path: 'transactionsWithOtherParts', label: '3.8.1 If yes, did you have transactions with other parts of the non-Hong Kong resident person for this year of assessment?', kind: 'yesNo', indent: 1},
        ],
    },
];

/** PTR2  -  labels from PTR.xlsx */
const PTR2_SECTIONS: SectionDef[] = [
    {
        title: 'PART 4     DETAILS OF THE CORPORATION',
        layout: 'descValue4to6',
        fields: [
        {path: 'postalAddress', label: '4.1 Postal address if different from that printed on this return :', kind: 'text'},
        {path: 'telephoneNumber', label: '4.2 Telephone Number :', kind: 'text'},
        {path: 'principalBusinessActivity', label: '4.3 Principal business activity :', kind: 'text'},
        // Excel AM8-AR8 HSIC: digit boxes only (no currency, no empty pads)
        {
            path: 'industrialClassificationCode',
            label: 'Hong Kong Standard Industrial Classification Code',
            kind: 'digitAmount',
            digitFilledOnly: true,
        },
        ],
    },
    {
        title: 'PART 5     RETURN FORM LANGUAGE',
        fields: [
        {path: 'receiveChineseReturn', label: 'If you wish to receive future Profits Tax Returns in CHINESE, "?" the box.', kind: 'checkbox'},
        ],
    },
    {
        title: 'PART 6     AUTHORIZED REPRESENTATIVE',
        titleHint: '(Complete only if you have appointed a representative.  Such an appointment is NOT compulsory)',
        layout: 'part6',
        fields: [
        {path: 'auditorEnglishName', label: 'hereby authorize', kind: 'text', valueAlign: 'left'},
        {path: 'authorizedRepresentativeAddress', label: 'of (Address)', kind: 'text', valueAlign: 'left'},
        {
            path: 'certificateNumber',
            label: 'The representative\'s Business Registration No. and Branch No., if any',
            kind: 'digitPair',
            valueAlign: 'right',
            descNowrap: true,
        },
        {
            path: 'representativeReferenceNo',
            label: 'The representative\'s Reference No.',
            kind: 'digitAmount',
            digitFilledOnly: true,
            digitBlankWhenEmpty: true,
            valueAlign: 'right',
            descNowrap: true,
        },
        ],
    },
    {
        title: 'PART 7     GENERAL MATTERS',
        fields: [
        {path: 'accountsAuditedRequired', label: '7.1 Are your accounts required to be audited by law?', kind: 'yesNo'},
        {path: 'accountsAuditedByHKUnit', label: '7.1.1 Are your accounts audited by a Hong Kong practice unit?', kind: 'yesNo', indent: 1},
        {
            path: 'pcNumber',
            label: '7.1.2 If the answer to item 7.1.1 is yes, state the practising certificate number of the certified public accountant (practising) who signed the Auditor\'s Report of which the certified copy is filed along this Profits Tax Return.',
            kind: 'digitAmount',
            digitFilledOnly: true,
            valueAlign: 'right',
            indent: 1,
        },
        // One row, two lines: From+To inline; accounting-date question + Yes/No
        {
            path: 'accountingDateDifferent',
            label: '7.2 State your basis period : From',
            kind: 'basisPeriod72',
        },
        // One row, two lines: Yes/No; commence date inline in description
        {
            path: 'commenceWithinPeriod',
            label: '7.3 Did you commence business within the basis period?',
            kind: 'yesNoFollowUp',
            followUpPath: 'commenceDate',
            followUpLabel: 'If yes, state the date of commencement :',
        },
        {path: 'cessationDuringThisPeriod', label: '7.4 Did you cease business within the basis period? If yes, complete Items 7.4.1, 7.4.2 and 7.4.3.', kind: 'yesNo'},
        {path: 'businessCessationDate', label: '7.4.1 State the date of cessation:', kind: 'text', indent: 1},
        {
            path: 'businessTransferredOnCessation',
            label: '7.4.2 On cessation, was your business or any part thereof transferred to and carried on by another person?',
            kind: 'yesNoFollowUp',
            followUpPath: 'businessTransfereeName',
            followUpLabel: 'If yes, state the name of this person:',
            indent: 1,
        },
        {path: 'assetsTransferredToAssociatedPerson', label: '7.4.3 On cessation, were any of the assets of your business sold or transferred to an associated person?', kind: 'yesNo', indent: 1},
        {path: 'alignFinancialInstrumentsTreatment', label: '7.5 Do you elect to align the tax treatment of financial instruments with their accounting treatment and have the related profits assessed in accordance with sections 181 to 18L of the Inland Revenue Ordinance?', kind: 'yesNo'},
        {
            path: 'financialStatementsInForeignCurrency',
            label: '7.6 Are your financial statements prepared in a foreign currency? If yes, state the currency and the conversion rate used to convert to HK dollars.',
            kind: 'yesNo',
            inlinePairs: [
                {label: 'Currency', path: 'foreignCurrency'},
                {label: 'Conversion rate', path: 'exchangeRate'},
            ],
        },
        {path: 'privateCompany', label: '7.7 Are you a private company? If yes, complete Item 7.7.1.', kind: 'yesNo'},
        {path: 'shareholdersChanged', label: '7.7.1 Has there been any change in your shareholders during the basis period?', kind: 'yesNo', indent: 1},
        {path: 'involvedInAmalgamation', label: '7.8 During the basis period, were you a party to an amalgamation under section 680 or 681 of the Companies Ordinance (Car. 622)?', kind: 'yesNo'},
        {path: 'involvedInMainlandofChina', label: '7.9 During the basis period, were you involved in any processing arrangement in the Mainland of China? If yes, complete Item 10.2.2.', kind: 'yesNo'},
        {path: 'insuranceCorpRBCRegime', label: '7.10 Are you an insurance corporation commencing to implement the risk-based capital ("RBC") regime to determine capital requirements for the basis period? If yes, complete Items 7.10.1 and 7.10.2.', kind: 'yesNo'},
        {
            path: 'rbcOneOffAdjustmentIncome',
            label: '7.10.1 Amount of one-off adjustment arising from the implementation of the RBC regime',
            kind: 'rbc7101',
            indent: 1,
        },
        {path: 'rbcAdjustmentFiveYearTreatment', label: '7.10.2 Do you elect to treat the one-off adjustment as your income or loss by 5 equal amounts for this year of assessment and the next succeeding 4 years of assessment?', kind: 'yesNo', indent: 1},
        {path: 'familyOwnedSpecialPurposeEntity', label: '7.11 Are you a family-owned special purpose entity in which an eligible family-owned investment holding vehicle has beneficial interest? If yes, complete Item 10.10.', kind: 'yesNo'},
        {path: 'eligibleSingleFamilyOffice', label: '7.12 Are you an eligible single family office of a family? If yes, submit the information as required in the Notes.', kind: 'yesNo'},
        ],
    },
    {
        title: 'PART 8     TRANSACTIONS FOR / WITH NON-RESIDENTS',
        fields: [
        {path: 'sellGoodsForNonResident', label: '8.1 During the basis period did you: 8.1.1 sell any goods or provide any services in Hong Kong on behalf of a non-resident person?', kind: 'yesNo'},
        {path: 'receiveIncomeForNonResident', label: '8.1 During the basis period did you: 8.1.2 receive, as agent, on behalf of a non-resident person any other trade or business income arising in or derived from Hong Kong?', kind: 'yesNo'},
        {path: 'valueCreationForNonResidentIP', label: '8.1 During the basis period did you: 8.1.3 make any value creation contributions in Hong Kong in relation to the intellectual property of a non-Hong Kong resident associate?', kind: 'yesNo'},
        {path: 'hireChargesToNonResidents', label: '8.2 Hire charges paid or accrued to non-resident persons for the use .of or right to use movable property in Hong Kong       If NIL, enter "0"', kind: 'digitAmount'},
        {path: 'professionalFeesToNonResidents', label: '8.3 Fees paid or accrued to non-resident persons in respect of professional services rendered in Hong Kong                 If NIL, enter "0"', kind: 'digitAmount'},
        ],
    },
];

/** PTR3  -  labels from PTR.xlsx */
const PTR3_SECTIONS: SectionDef[] = [
    {
        title: 'PART 9\'     DETAILS OF SUPPLEMENTARY FORM(S) UPLOADED (See Note C3 for details.)',
        note: 'If fall within any of the descriptions in Part 9 of the Notes, please download the relevant supplementary form from Department\'s web site (wwvv.ird.gov.hk/e_pfr) for electronic completion, sign and submit a Control List.',
        layout: 'dualColCheckbox',
        fields: [
        {path: 'formS1Selected', label: '9.1 Form S1 - Person electing for two-tiered profits tax rates', kind: 'checkbox'},
        {path: 'formS2Selected', label: '9.2 Form S2 -Transfer pricing', kind: 'checkbox'},
        {path: 'formS3Selected', label: '9.3 Form S3 - Expenditure on research & development', kind: 'checkbox'},
        {path: 'formS4Selected', label: '9.4 Form S4 - Expenditure on energy efficient building installation', kind: 'checkbox'},
        {path: 'formS5Selected', label: '9.5 Form S5 - Ship-owner', kind: 'checkbox'},
        {path: 'formS6Selected', label: '9.6 Form S6 - Professional reinsurer', kind: 'checkbox'},
        {path: 'formS7Selected', label: '9.7 Form S7 - Authorized captive insurer', kind: 'checkbox'},
        {path: 'formS8Selected', label: '9.8 Form S8 - Qualifying corporate treasury centre', kind: 'checkbox'},
        {path: 'formS9Selected', label: '9.9 Form S9 - Qualifying aircraft lessor', kind: 'checkbox'},
        {path: 'formS10Selected', label: '9.10 Form S10 - Qualifying aircraft leasing manager', kind: 'checkbox'},
        {path: 'formS11Selected', label: '9.11 Form S11 - Qualifying ship lessor', kind: 'checkbox'},
        {path: 'formS12Selected', label: '9.12 Form S12 - Qualifying ship leasing manager', kind: 'checkbox'},
        {path: 'formS13Selected', label: '9.13 Form S13 - Specified insurer', kind: 'checkbox'},
        {path: 'formS14Selected', label: '9.14 Form S14 - Licensed insurance broker company', kind: 'checkbox'},
        {path: 'formS15Selected', label: '9.15 Form S15 - Persons deriving eligible carried interest', kind: 'checkbox'},
        {path: 'formS16Selected', label: '9.16 Form S16 - Qualifying ship agent', kind: 'checkbox'},
        {path: 'formS17Selected', label: '9.17 Form S17 - Qualifying ship manager', kind: 'checkbox'},
        {path: 'formS18Selected', label: '9.18 Form S18 - Qualifying ship broker', kind: 'checkbox'},
        {path: 'formS19Selected', label: '9.19 Form S19 - Taxation on specified foreign-sourced income', kind: 'checkbox'},
        {path: 'formS20Selected', label: '9.20 Form S20 -Tax concessions for family-owned investment holding vehicle', kind: 'checkbox'},
        {path: 'formS21Selected', label: '9.21 Form S21 - Tax certainty enhancement scheme for onshore gain on disposal of equity interest', kind: 'checkbox'},
        {path: 'formS22Selected', label: '9.22 Form S22 - Tax concessions for intellectual property income', kind: 'checkbox'},
        ],
    },
    {
        title: 'PART 10     TAX DATA (Complete all items.  If NIL, enter "0"',
        fields: [
        {path: 'offshoreProfitsExcluded', label: '10.1 Offshore profits excluded from the Assessable Profits or Adjusted Loss stated in Part 1', kind: 'digitAmount'},
        {path: 'offshoreBizProfitDetail.internetRelatedProfits', label: '10.2 Offshore profits from business (already included in Item 10.1) attributable to:  -  10.2.1 the use of the Internet to accept orders, sell goods, provide services or accept payment', kind: 'digitAmount', indent: 1},
        {path: 'offshoreBizProfitDetail.mainlandProcessingProfits', label: '10.2 Offshore profits from business (already included in Item 10.1) attributable to:  -  10.2.2 contract processing or import processing arrangement in the Mainland of China', kind: 'digitAmount', indent: 1},
        {path: 'hkLandedPropertySaleProfitsExcluded', label: '10.3 Profits from sale of landed properties in Hong Kong excluded from the Assessable Profits or Adjusted Loss stated in Part 1', kind: 'digitAmount'},
        {path: 'capitalAssetSaleProfitsExcluded', label: '10.4 Profits from sale of capital assets (other than landed properties in Hong Kong) excluded from the Assessable Profits or Adjusted Loss stated in Part 1', kind: 'digitAmount'},
        {path: 'taxExemptNetInterestIncome', label: '10.5 Net interest income exempted from payment of Profits Tax', kind: 'digitAmount'},
        {path: 'taxExemptQualifyingDebtIncome', label: '10.6 Interest, profits or gains from qualifying debt instruments (issued on or after 1 April 2018) exempted from payment of Profits Tax', kind: 'digitAmount'},
        {path: 'taxExemptSch16CAssetTransProfits', label: '10.7 Profits from transactions in assets of a class specified in Schedule 16C to the Inland Revenue Ordinance and incidental transactions exempted from payment of Profits Tax', kind: 'digitAmount'},
        {path: 'taxExemptSpecSecuritiesTransProfits', label: '10.8 Profits from transactions in relation to specified securities exempted from payment of Profits Tax', kind: 'digitAmount'},
        {path: 'familySpvConcessionaryRateProfits', label: '10.9 Profits earned by a family-owned special purpose entity from transactions specified in section 16(3) of Schedule 16E to the Inland Revenue Ordinance chargeable at concessionary tax rate', kind: 'digitAmount'},
        {path: 'approvedCharityDonationDeduction', label: '10.10 Deduction claimed for approved charitable donations', kind: 'digitAmount'},
        {path: 'buildingRefurbDeduction', label: '10.11 Deduction claimed for expenditure on building refurbishment', kind: 'digitAmount'},
        {path: 'computerHardwareSoftwareDeduction', label: '10.12 Deduction claimed for expenditure on computer hardware and software', kind: 'digitAmount'},
        {path: 'prescribedManufacturingMachDeduction', label: '10.13 Deduction claimed for expenditure on prescribed manufacturing machinery or plant', kind: 'digitAmount'},
        {path: 'envProtectionMachDeduction', label: '10.14 Deduction claimed for expenditure on environmental protection machinery', kind: 'digitAmount'},
        {path: 'envProtectionInstallDeduction', label: '10.15 Deduction claimed for expenditure on environmental protection installation', kind: 'digitAmount'},
        {path: 'envFriendlyVehicleDeduction', label: '10.16 Deduction claimed for expenditure on environment-friendly vehicles', kind: 'digitAmount'},
        {path: 'ipDeduction.patentRightExpense', label: '10.17 Deduction claimed for expenditure on:  -  10.17.1 Patent rights', kind: 'digitAmount', indent: 1},
        {path: 'ipDeduction.knowHowRightExpense', label: '10.17 Deduction claimed for expenditure on:  -  10.17.2 rights to know-how', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.copyrightExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.1 copyrights', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.performerEconomicRightExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.2 performer\'s economic rights', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.protectedLayoutDesignExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.3 protected layout-design (topography) rights', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.protectedPlantVarietyExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.4 protected plant variety rights', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.registeredDesignExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.5 registered designs', kind: 'digitAmount', indent: 1},
        {path: 'specifiedIpDeduction.registeredTrademarkExpense', label: '10.18 Deduction claimed for specified expenditure on:  -  10.18.6 registered trade marks', kind: 'digitAmount', indent: 1},
        {path: 'taxReliefArrangement.foreignTaxCreditClaimed', label: '10.19 Tax relief claimed pursuant to an arrangement stated in Item 3.4:  -  10.19.1 foreign tax paid claimed as a tax credit\\', kind: 'digitAmount', indent: 1},
        {path: 'taxReliefArrangement.incomeProfitsExcluded', label: '10.19 Tax relief claimed pursuant to an arrangement stated in Item 3.4:  -  10.19.2 income or profits excluded from the Assessable Profits or Adjusted Loss stated in Part 1', kind: 'digitAmount', indent: 1},
        {path: 'intraGroupFinancingInterestDeduction', label: '10.20 Deduction claimed for interest to non-Hong Kong associations corporations in the ordinary course of an intra-group financing business', kind: 'digitAmount'},
        {path: 'leasedPremisesReinstatementDeduction', label: '10.21 Deduction claimed for leased premises reinstatement costs', kind: 'digitAmount'},
        ],
    },
];

/** PTR4  -  labels from PTR.xlsx */
const PTR4_SECTIONS: SectionDef[] = [
    {
        title: 'PART 11     DEPRECIATION ALLOWANCES CLAIMED (Complete all items. If NIL, enter "0".)',
        layout: 'part11',
        fields: [
        {path: 'industrialBuildingInitialAllowance', label: '11.1 Initial allowance (Industrial Building)', kind: 'digitAmount'},
        {path: 'industrialBuildingAnnualAllowance', label: '11.2 Annual Allowance (Industrial Building)', kind: 'digitAmount'},
        {path: 'industrialBuildingBalancingAllowance', label: '11.3 Balancing Allowance (Industrial Building)', kind: 'digitAmount'},
        {path: 'industrialBuildingBalancingCharge', label: '11.4 Balancing Charge (Industrial Building)', kind: 'digitAmount'},
        {path: 'commercialBuildingAnnualAllowance', label: '11.5 Annual Allowance (Commercial Building)', kind: 'digitAmount'},
        {path: 'commercialBuildingBalancingAllowance', label: '11.6 Balancing Allowance (Commercial Building)', kind: 'digitAmount'},
        {path: 'commercialBuildingBalancingCharge', label: '11.7 Balancing Charge (Commercial Building)', kind: 'digitAmount'},
        {path: 'machineryPlantInitialAllowance', label: '11.8 Initial Allowance (Machinery or Plant)', kind: 'digitAmount'},
        {path: 'machineryPlantAnnualAllowance', label: '11.9 Annual Allowance (Machinery or Plant)', kind: 'digitAmount'},
        {path: 'machineryPlantBalancingAllowance', label: '11.10 Balancing Allowance (Machinery or Plant)', kind: 'digitAmount'},
        {path: 'machineryPlantBalancingCharge', label: '11.11 Balancing charge (Machinery or Plant)', kind: 'digitAmount'},
        ],
    },
    {
        title: 'PART 12     FINANCIAL DATA (Complete all items. If NIL, enter "0".)',
        layout: 'dualColDigit',
        fields: [
        {path: 'grossIncome', label: '12.1 Gross income', kind: 'digitAmount'},
        {path: 'turnover', label: '12.2 Turnover', kind: 'digitAmount'},
        {path: 'openingInventories', label: '12.3 Opening inventories', kind: 'digitAmount'},
        {path: 'purchases', label: '12.4 Purchases', kind: 'digitAmount'},
        {path: 'closingInventories', label: '12.5 Closing inventories', kind: 'digitAmount'},
        {path: 'grossProfit', label: '12.6 Gross profit', kind: 'digitAmount'},
        {path: 'grossLoss', label: '12.7 Gross loss', kind: 'digitAmount'},
        {path: 'dividendIncome', label: '12.8 Dividend income', kind: 'digitAmount'},
        {path: 'interestIncome', label: '12.9 Interest inccome', kind: 'digitAmount'},
        {path: 'interestExpenses', label: '12.10 Interest expenses', kind: 'digitAmount'},
        {path: 'employeeDirectorRemuneration', label: '12.11 Employee and director remuneration', kind: 'digitAmount'},
        {path: 'shareBasedPayments', label: '12.12 Share-based payments', kind: 'digitAmount'},
        {path: 'commissionPayments', label: '12.13 Commission payments', kind: 'digitAmount'},
        {path: 'intellectualPropertyPayments', label: '12.14 Intellectual property payments', kind: 'digitAmount'},
        {path: 'managementConsultancyFeePayments', label: '12.15 Management and consultancy fee payments', kind: 'digitAmount'},
        {path: 'contractorSubcontractorCharges', label: '12.16 Contractor and subcontractor charges', kind: 'digitAmount'},
        {path: 'badDebts', label: '12.17 Bad debts', kind: 'digitAmount'},
        {path: 'netProfitPerAccount', label: '12.18 Net profit per account', kind: 'digitAmount'},
        {path: 'netLossPerAccount', label: '12.19 Net loss per account', kind: 'digitAmount'},
        {path: 'accountsReceivableTrade', label: '12.20 Accounts receivable (trade)', kind: 'digitAmount'},
        {path: 'accountsPayableTrade', label: '12.21 Accounts payable (trade)', kind: 'digitAmount'},
        {path: 'issuedShareCapital', label: '12.22 Issued share capital', kind: 'digitAmount'},
        {path: 'totalValueOfAssets', label: '12.23 Total value of assets', kind: 'digitAmount'},
        ],
    },
];

const SHEET_SECTIONS: Record<PtrSheet, SectionDef[]> = {
    PTR1: PTR1_SECTIONS,
    PTR2: PTR2_SECTIONS,
    PTR3: PTR3_SECTIONS,
    PTR4: PTR4_SECTIONS,
};

function getByPath(source: unknown, path: string): unknown {
    if (source == null || typeof source !== 'object') return undefined;
    return path.split('.').reduce<unknown>((acc, key) => {
        if (acc == null || typeof acc !== 'object') return undefined;
        return (acc as Record<string, unknown>)[key];
    }, source);
}

function formatDisplay(value: unknown, kind: FieldKind): string {
    if (kind === 'yesNo') return '-';
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'object') return '-';
    return String(value);
}

/** Map VO 1/0 to RadioGroup value; empty when unset. */
function toYesNoRadioValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    if (n === 1) return '1';
    if (n === 0) return '0';
    return '';
}

/** Read-only Yes/No radios (PTR Part 2.3 / Part 3 style), right-aligned. */
function PtrYesNoRadioGroup({value, compact = false}: {value: unknown; compact?: boolean}) {
    const labelSx = {
        mr: compact ? 0.5 : 1,
        ml: 0,
        my: compact ? 0 : undefined,
        '& .MuiFormControlLabel-label': {fontSize: 13, lineHeight: compact ? 1.2 : undefined},
    };
    return (
        <RadioGroup
            row
            value={toYesNoRadioValue(value)}
            sx={{
                display: 'inline-flex',
                justifyContent: 'flex-end',
                flexWrap: 'nowrap',
                m: 0,
                gap: compact ? 0.25 : 0.5,
                pointerEvents: 'none',
            }}
        >
            <FormControlLabel
                value="1"
                control={<Radio size="small" sx={compact ? {p: 0.25} : undefined}/>}
                label="Yes"
                sx={{...labelSx, mr: compact ? 0.5 : 1}}
            />
            <FormControlLabel
                value="0"
                control={<Radio size="small" sx={compact ? {p: 0.25} : undefined}/>}
                label="No"
                sx={{...labelSx, mr: 0}}
            />
        </RadioGroup>
    );
}

/** Read-only checkbox for Part 9 S-form selection (1 = checked). */
function PtrCheckboxValue({value}: {value: unknown}) {
    const checked = Number(value) === 1;
    return (
        <Checkbox
            size="small"
            checked={checked}
            tabIndex={-1}
            disableRipple
            sx={{p: 0.25, pointerEvents: 'none'}}
            inputProps={{readOnly: true, 'aria-readonly': true}}
        />
    );
}

const PART9_LEFT_COUNT = 11; // 9.1-9.11
const PART9_NO_COL_WIDTH = 96;
const PART9_CHECK_COL_WIDTH = 36;
const PART12_LEFT_COUNT = 12; // 12.1-12.12
const PART12_DIGIT_COUNT = 23; // 12.1-12.23 (Director / Company name below as Typography)
/** Narrow cols so PTR4 dual tables fit without horizontal scroll. */
const PART12_NO_COL_WIDTH = 72;
const PART12_VALUE_COL_WIDTH = 178;
/** Leading spaces before item no (PTR1-4 / Part 9). */
const ITEM_NO_LEADING = '        '; // 8 spaces (x.x)
const ITEM_NO_SUB_LEADING = '        '; // +8 spaces for x.x.x sub-items

/** x.x ? 8 spaces; x.x.x+ ? 16 spaces (sub-item indent). */
function formatItemNoDisplay(itemNo: string): string {
    if (!itemNo) return '';
    const isSubItem = itemNo.split('.').length >= 3;
    return `${ITEM_NO_LEADING}${isSubItem ? ITEM_NO_SUB_LEADING : ''}${itemNo}`;
}

/** Excel Part 9 dual columns: left 9.1-9.11, right 9.12-9.22. */
function Part9DualCheckboxTable({
    fields,
    sheetData,
}: {
    fields: FieldDef[];
    sheetData: Record<string, unknown>;
}) {
    const left = fields.slice(0, PART9_LEFT_COUNT);
    const right = fields.slice(PART9_LEFT_COUNT);
    const rowCount = Math.max(left.length, right.length);

    const renderSideCells = (field: FieldDef | undefined, keyPrefix: string) => {
        if (!field) {
            return (
                <>
                    <Box component="td" key={`${keyPrefix}-no`}/>
                    <Box component="td" key={`${keyPrefix}-text`}/>
                    <Box component="td" key={`${keyPrefix}-val`}/>
                </>
            );
        }
        const {no: itemNo, text: itemText} = splitItemNo(field.label);
        const raw = getByPath(sheetData, field.path);
        return (
            <>
                <Box
                    component="td"
                    sx={{
                        pr: 1,
                        color: 'text.secondary',
                        fontSize: 13,
                        fontWeight: 700,
                        whiteSpace: 'pre',
                        fontVariantNumeric: 'tabular-nums',
                        verticalAlign: 'middle',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 0.15,
                        lineHeight: 1.25,
                    }}
                >
                    {formatItemNoDisplay(itemNo)}
                </Box>
                <Box
                    component="td"
                    sx={{
                        pr: 1.5,
                        color: 'text.primary',
                        fontSize: 13,
                        textAlign: 'left',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        verticalAlign: 'middle',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 0.15,
                        lineHeight: 1.25,
                    }}
                >
                    {itemText}
                </Box>
                <Box
                    component="td"
                    sx={{
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        py: 0.15,
                        lineHeight: 1.25,
                    }}
                >
                    <PtrCheckboxValue value={raw}/>
                </Box>
            </>
        );
    };

    return (
        <Box
            component="table"
            sx={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed'}}
        >
            <Box component="colgroup">
                <Box component="col" sx={{width: PART9_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART9_CHECK_COL_WIDTH}}/>
                <Box component="col" sx={{width: PART9_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART9_CHECK_COL_WIDTH}}/>
            </Box>
            <Box component="tbody">
                {Array.from({length: rowCount}, (_, i) => (
                    <Box component="tr" key={`part9-row-${i}`}>
                        {renderSideCells(left[i], `L${i}`)}
                        {renderSideCells(right[i], `R${i}`)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

const DUAL_CELL_SX = {
    borderBottom: '1px solid',
    borderColor: 'divider',
    py: 0.15,
    lineHeight: 1.25,
    verticalAlign: 'middle' as const,
};

/** Shared dual-col digit side cells (Part 11 / 12). */
function renderDualDigitSideCells(
    field: FieldDef | undefined,
    keyPrefix: string,
    sheetData: Record<string, unknown>,
    presentationCurrency: string,
) {
    if (!field) {
        return (
            <>
                <Box component="td" key={`${keyPrefix}-no`}/>
                <Box component="td" key={`${keyPrefix}-text`}/>
                <Box component="td" key={`${keyPrefix}-val`}/>
            </>
        );
    }
    const {no: itemNo, text: itemText} = splitItemNo(field.label);
    const raw = getByPath(sheetData, field.path);
    return (
        <>
            <Box
                component="td"
                sx={{
                    ...DUAL_CELL_SX,
                    pr: 1,
                    color: 'text.secondary',
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: 'pre',
                    fontVariantNumeric: 'tabular-nums',
                }}
            >
                {formatItemNoDisplay(itemNo)}
            </Box>
            <Box
                component="td"
                sx={{
                    ...DUAL_CELL_SX,
                    pr: 1.5,
                    color: 'text.primary',
                    fontSize: 13,
                    textAlign: 'left',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {itemText}
            </Box>
            <Box component="td" sx={{...DUAL_CELL_SX, textAlign: 'right', overflow: 'hidden'}}>
                <DigitAmountBoxes value={raw} currency={presentationCurrency} dense/>
            </Box>
        </>
    );
}

function renderDualSectionTitle(leftTitle: string, rightTitle: string) {
    return (
        <Box component="tr">
            <Box component="td" sx={DUAL_CELL_SX}/>
            <Box
                component="td"
                colSpan={2}
                sx={{
                    ...DUAL_CELL_SX,
                    color: 'text.primary',
                    fontSize: 13,
                    fontWeight: 700,
                }}
            >
                {leftTitle}
            </Box>
            <Box component="td" sx={DUAL_CELL_SX}/>
            <Box
                component="td"
                colSpan={2}
                sx={{
                    ...DUAL_CELL_SX,
                    color: 'text.primary',
                    fontSize: 13,
                    fontWeight: 700,
                }}
            >
                {rightTitle}
            </Box>
        </Box>
    );
}

/**
 * Part 11 dual columns:
 * rows 11.1-11.4 left only; then header Commercial Building | Machinery or Plant;
 * then left 11.5-11.7 with right 11.8-11.11.
 */
function Part11DualDigitTable({
    fields,
    sheetData,
    presentationCurrency,
}: {
    fields: FieldDef[];
    sheetData: Record<string, unknown>;
    presentationCurrency: string;
}) {
    const industrial = fields.slice(0, 4); // 11.1-11.4
    const commercial = fields.slice(4, 7); // 11.5-11.7
    const machinery = fields.slice(7, 11); // 11.8-11.11
    const afterHeaderRows = Math.max(commercial.length, machinery.length);

    return (
        <Box
            component="table"
            sx={{
                width: '100%',
                maxWidth: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
            }}
        >
            <Box component="colgroup">
                <Box component="col" sx={{width: PART12_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART12_VALUE_COL_WIDTH}}/>
                <Box component="col" sx={{width: PART12_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART12_VALUE_COL_WIDTH}}/>
            </Box>
            <Box component="tbody">
                {industrial.map((field, i) => (
                    <Box component="tr" key={`part11-ind-${field.path}`}>
                        {renderDualDigitSideCells(field, `IL${i}`, sheetData, presentationCurrency)}
                        {renderDualDigitSideCells(undefined, `IR${i}`, sheetData, presentationCurrency)}
                    </Box>
                ))}
                {renderDualSectionTitle('Commercial Building', 'Machinery or Plant')}
                {Array.from({length: afterHeaderRows}, (_, i) => (
                    <Box component="tr" key={`part11-after-${i}`}>
                        {renderDualDigitSideCells(commercial[i], `CL${i}`, sheetData, presentationCurrency)}
                        {renderDualDigitSideCells(machinery[i], `MR${i}`, sheetData, presentationCurrency)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/** Excel Part 12 dual columns: left 12.1-12.12, right 12.13-12.23. */
function Part12DualDigitTable({
    fields,
    sheetData,
    presentationCurrency,
}: {
    fields: FieldDef[];
    sheetData: Record<string, unknown>;
    presentationCurrency: string;
}) {
    const left = fields.slice(0, PART12_LEFT_COUNT);
    const right = fields.slice(PART12_LEFT_COUNT);
    const rowCount = Math.max(left.length, right.length);

    return (
        <Box
            component="table"
            sx={{
                width: '100%',
                maxWidth: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
            }}
        >
            <Box component="colgroup">
                <Box component="col" sx={{width: PART12_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART12_VALUE_COL_WIDTH}}/>
                <Box component="col" sx={{width: PART12_NO_COL_WIDTH}}/>
                <Box component="col"/>
                <Box component="col" sx={{width: PART12_VALUE_COL_WIDTH}}/>
            </Box>
            <Box component="tbody">
                {Array.from({length: rowCount}, (_, i) => (
                    <Box component="tr" key={`part12-row-${i}`}>
                        {renderDualDigitSideCells(left[i], `L${i}`, sheetData, presentationCurrency)}
                        {renderDualDigitSideCells(right[i], `R${i}`, sheetData, presentationCurrency)}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

const ITEM_NO_RE = /^(\d+(?:\.\d+)*)\s+(.*)$/;
/** Nested: "10.17 ...:  -  10.17.1 Patent" / "8.1 ...: 8.1.1 sell" */
const ITEM_NO_AFTER_SEP_RE = /(?::\s*(?:-\s*)?|\s-\s+)(\d+(?:\.\d+)*)\s+(.+)$/;

/**
 * Split Excel item no (1.1 / 7.1.2 / 10.17.1) from description.
 * Nested labels use the sub-item no after ":" / " - ".
 */
function splitItemNo(label: string): {no: string; text: string} {
    const afterSep = ITEM_NO_AFTER_SEP_RE.exec(label);
    if (afterSep && ITEM_NO_RE.test(label)) {
        return {no: afterSep[1], text: afterSep[2]};
    }
    const lead = ITEM_NO_RE.exec(label);
    if (lead) return {no: lead[1], text: lead[2]};
    return {no: '', text: label};
}

/** Shared column widths for one tab-level table (Part 1/2/3 share the same vertical rails). */
const ITEM_NO_COL_WIDTH = 152;
const VALUE_COL_WIDTH = 280;

/** Split BR No. / Branch No. on first "-". */
function splitDigitPair(value: unknown): [string, string] {
    const s = String(value ?? '').trim();
    const i = s.indexOf('-');
    if (i < 0) return [s, ''];
    return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
}

/** Two filled-only DigitAmountBoxes with "-" between (Part 6 BR / Branch). */
function PtrDigitPairValue({value}: {value: unknown}) {
    const [left, right] = splitDigitPair(value);
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.75,
            }}
        >
            <DigitAmountBoxes value={left} filledOnly/>
            <Typography component="span" sx={{fontSize: 13, fontWeight: 700, lineHeight: 1}}>
                -
            </Typography>
            <DigitAmountBoxes value={right} filledOnly/>
        </Box>
    );
}

function resolveValueAlign(fieldItem: FieldDef): 'left' | 'right' {
    if (fieldItem.valueAlign) return fieldItem.valueAlign;
    if (
        fieldItem.kind === 'amount'
        || fieldItem.kind === 'digitAmount'
        || fieldItem.kind === 'digitPair'
        || fieldItem.kind === 'yesNo'
        || fieldItem.kind === 'checkbox'
    ) {
        return 'right';
    }
    return 'left';
}

/** Nested section table with description:value percent widths. */
function PtrRatioFieldTable({
    fields,
    sheetData,
    presentationCurrency,
    descPercent,
    valuePercent,
    compact = false,
}: {
    fields: FieldDef[];
    sheetData: Record<string, unknown>;
    presentationCurrency: string;
    descPercent: number;
    valuePercent: number;
    compact?: boolean;
}) {
    return (
        <Box
            component="table"
            sx={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed'}}
        >
            <Box component="colgroup">
                <Box component="col" sx={{width: ITEM_NO_COL_WIDTH}}/>
                <Box component="col" sx={{width: `${descPercent}%`}}/>
                <Box component="col" sx={{width: `${valuePercent}%`}}/>
            </Box>
            <Box component="tbody">
                {fields.map((fieldItem) => (
                    <PtrFieldRow
                        key={fieldItem.path}
                        fieldItem={fieldItem}
                        sheetData={sheetData}
                        presentationCurrency={presentationCurrency}
                        compact={compact}
                    />
                ))}
            </Box>
        </Box>
    );
}

function inlineTextValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
}

const PTR_COMPOSITE_NO_SX = {
    pr: 1,
    pl: 0,
    color: 'text.secondary',
    fontSize: 13,
    fontWeight: 700,
    whiteSpace: 'pre' as const,
    fontVariantNumeric: 'tabular-nums' as const,
    textAlign: 'left' as const,
};

const PTR_COMPOSITE_DESC_SX = {
    pr: 2,
    pl: 0,
    color: 'text.primary',
    fontSize: 13,
    textAlign: 'left' as const,
};

/** Part 7.2: two lines in one row — From/To dates; accounting-date Yes/No. */
function PtrBasisPeriod72Row({
    sheetData,
    compact = false,
}: {
    sheetData: Record<string, unknown>;
    compact?: boolean;
}) {
    const periodFrom = inlineTextValue(getByPath(sheetData, 'periodFrom'));
    const periodTo = inlineTextValue(getByPath(sheetData, 'periodTo'));
    const accountingDateDifferent = getByPath(sheetData, 'accountingDateDifferent');
    const subLineMt = compact ? 0.15 : 0.75;
    return (
        <Box
            component="tr"
            sx={{
                '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: compact ? 0.15 : 0.75,
                    verticalAlign: 'top',
                    lineHeight: compact ? 1.25 : undefined,
                },
            }}
        >
            <Box component="td" sx={PTR_COMPOSITE_NO_SX}>
                {formatItemNoDisplay('7.2')}
            </Box>
            <Box component="td" sx={PTR_COMPOSITE_DESC_SX}>
                <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                    {'State your basis period : From '}
                    <Box component="span" sx={{fontWeight: 700}}>{periodFrom}</Box>
                    {' To '}
                    <Box component="span" sx={{fontWeight: 700}}>{periodTo}</Box>
                </Box>
                <Box sx={{mt: subLineMt, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                    is the accounting date for this year different from that of last year?
                </Box>
            </Box>
            <Box
                component="td"
                sx={{
                    textAlign: 'right',
                    fontSize: 13,
                    fontWeight: 500,
                    verticalAlign: 'bottom',
                }}
            >
                <PtrYesNoRadioGroup value={accountingDateDifferent} compact={compact}/>
            </Box>
        </Box>
    );
}

/**
 * Yes/No on line 1; follow-up label + bold value inline on line 2 (description col).
 * Used by Part 7.3 / 7.4.2.
 */
function PtrYesNoFollowUpRow({
    fieldItem,
    sheetData,
    compact = false,
}: {
    fieldItem: FieldDef;
    sheetData: Record<string, unknown>;
    compact?: boolean;
}) {
    const yesNoValue = getByPath(sheetData, fieldItem.path);
    const followUpValue = inlineTextValue(
        fieldItem.followUpPath ? getByPath(sheetData, fieldItem.followUpPath) : undefined,
    );
    const {no: itemNo, text: itemText} = splitItemNo(fieldItem.label);
    const followUpLabel = (fieldItem.followUpLabel || '').trimEnd();
    const subLineMt = compact ? 0.15 : 0.75;
    return (
        <Box
            component="tr"
            sx={{
                '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: compact ? 0.15 : 0.75,
                    verticalAlign: 'top',
                    lineHeight: compact ? 1.25 : undefined,
                },
            }}
        >
            <Box component="td" sx={PTR_COMPOSITE_NO_SX}>
                {formatItemNoDisplay(itemNo)}
            </Box>
            <Box component="td" sx={PTR_COMPOSITE_DESC_SX}>
                <Box sx={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                    {itemText}
                </Box>
                <Box sx={{mt: subLineMt, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                    {`${followUpLabel} `}
                    <Box component="span" sx={{fontWeight: 700}}>{followUpValue}</Box>
                </Box>
            </Box>
            <Box
                component="td"
                sx={{
                    textAlign: 'right',
                    fontSize: 13,
                    fontWeight: 500,
                    verticalAlign: 'top',
                }}
            >
                <PtrYesNoRadioGroup value={yesNoValue} compact={compact}/>
            </Box>
        </Box>
    );
}

const RBC_NIL_SUFFIX_COL_WIDTH = 220;

function ptrCompositeTd(compact: boolean) {
    return {
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: compact ? 0.15 : 0.75,
        verticalAlign: 'top' as const,
        fontSize: 13,
        lineHeight: compact ? 1.25 : undefined,
    };
}

/** Part 7.10.1: Income + Loss suffixes share one column; DigitAmountBoxes with currency. */
function PtrRbc7101Rows({
    sheetData,
    presentationCurrency,
    compact = false,
}: {
    sheetData: Record<string, unknown>;
    presentationCurrency: string;
    compact?: boolean;
}) {
    const income = getByPath(sheetData, 'rbcOneOffAdjustmentIncome');
    const loss = getByPath(sheetData, 'rbcOneOffAdjustmentLoss');
    const td = ptrCompositeTd(compact);
    return (
        <Box component="tr">
            <Box component="td" colSpan={3} sx={{p: 0}}>
                <Box
                    component="table"
                    sx={{width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed'}}
                >
                    <Box component="colgroup">
                        <Box component="col" sx={{width: ITEM_NO_COL_WIDTH}}/>
                        <Box component="col"/>
                        <Box component="col" sx={{width: RBC_NIL_SUFFIX_COL_WIDTH}}/>
                        <Box component="col" sx={{width: VALUE_COL_WIDTH}}/>
                    </Box>
                    <Box component="tbody">
                        <Box component="tr">
                            <Box
                                component="td"
                                sx={{...PTR_COMPOSITE_NO_SX, ...td, borderBottom: 'none'}}
                            >
                                {formatItemNoDisplay('7.10.1')}
                            </Box>
                            <Box
                                component="td"
                                sx={{...PTR_COMPOSITE_DESC_SX, ...td, borderBottom: 'none'}}
                            >
                                Amount of one-off adjustment arising from the implementation of the RBC regime
                            </Box>
                            <Box
                                component="td"
                                sx={{
                                    ...td,
                                    borderBottom: 'none',
                                    whiteSpace: 'nowrap',
                                    color: 'text.primary',
                                }}
                            >
                                Income
                            </Box>
                            <Box
                                component="td"
                                sx={{...td, borderBottom: 'none', textAlign: 'right'}}
                            >
                                <DigitAmountBoxes
                                    value={income}
                                    currency={presentationCurrency}
                                    blankWhenEmpty
                                />
                            </Box>
                        </Box>
                        <Box component="tr">
                            <Box component="td" sx={{...PTR_COMPOSITE_NO_SX, ...td}}/>
                            <Box component="td" sx={{...PTR_COMPOSITE_DESC_SX, ...td}}>
                                {'If NIL, enter "0"'}
                            </Box>
                            <Box
                                component="td"
                                sx={{...td, whiteSpace: 'nowrap', color: 'text.primary'}}
                            >
                                Loss
                            </Box>
                            <Box component="td" sx={{...td, textAlign: 'right'}}>
                                <DigitAmountBoxes
                                    value={loss}
                                    currency={presentationCurrency}
                                    blankWhenEmpty
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

/** One PTR form row: item no | description | value. */
function PtrFieldRow({
    fieldItem,
    sheetData,
    presentationCurrency,
    compact = false,
}: {
    fieldItem: FieldDef;
    sheetData: Record<string, unknown>;
    presentationCurrency: string;
    /** PTR2 single-line rows: minimal vertical padding. */
    compact?: boolean;
}) {
    if (fieldItem.kind === 'basisPeriod72') {
        return <PtrBasisPeriod72Row sheetData={sheetData} compact={compact}/>;
    }
    if (fieldItem.kind === 'yesNoFollowUp') {
        return (
            <PtrYesNoFollowUpRow
                fieldItem={fieldItem}
                sheetData={sheetData}
                compact={compact}
            />
        );
    }
    if (fieldItem.kind === 'rbc7101') {
        return (
            <PtrRbc7101Rows
                sheetData={sheetData}
                presentationCurrency={presentationCurrency}
                compact={compact}
            />
        );
    }
    const raw = getByPath(sheetData, fieldItem.path);
    const display = formatDisplay(raw, fieldItem.kind);
    const {no: itemNo, text: itemText} = splitItemNo(fieldItem.label);
    const isDigitAmount = fieldItem.kind === 'digitAmount';
    const isDigitPair = fieldItem.kind === 'digitPair';
    const isYesNo = fieldItem.kind === 'yesNo';
    const isCheckbox = fieldItem.kind === 'checkbox';
    const itemNoDisplay = formatItemNoDisplay(itemNo);
    const currencyPairValue = fieldItem.inlinePairs?.[0]
        ? inlineTextValue(getByPath(sheetData, fieldItem.inlinePairs[0].path))
        : '';
    return (
        <Box
            component="tr"
            sx={{
                '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: compact ? 0.15 : 0.75,
                    verticalAlign: compact ? 'middle' : 'top',
                    lineHeight: compact ? 1.25 : undefined,
                },
            }}
        >
            <Box
                component="td"
                sx={{
                    pr: 1,
                    pl: 0,
                    color: 'text.secondary',
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: 'pre',
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'left',
                }}
            >
                {itemNoDisplay}
            </Box>
            <Box
                component="td"
                sx={{
                    pr: 2,
                    pl: 0,
                    color: 'text.primary',
                    fontSize: 13,
                    textAlign: 'left',
                    whiteSpace: fieldItem.descNowrap ? 'nowrap' : 'pre-wrap',
                    wordBreak: fieldItem.descNowrap ? 'normal' : 'break-word',
                }}
            >
                {itemText}
                {fieldItem.inlinePairs?.map((pair, pairIndex) => (
                    <React.Fragment key={pair.path}>
                        {'    '}
                        {`${pair.label}: `}
                        <Box component="span" sx={{fontWeight: 700}}>
                            {pairIndex > 0 && currencyPairValue === '-'
                                ? '-'
                                : inlineTextValue(getByPath(sheetData, pair.path))}
                        </Box>
                    </React.Fragment>
                ))}
            </Box>
            <Box
                component="td"
                sx={{
                    textAlign: resolveValueAlign(fieldItem),
                    fontVariantNumeric: fieldItem.kind === 'amount' ? 'tabular-nums' : 'normal',
                    fontSize: 13,
                    fontWeight: 500,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {isDigitAmount ? (
                    <DigitAmountBoxes
                        value={raw}
                        {...(fieldItem.digitFilledOnly || fieldItem.digitBlankWhenEmpty
                            ? {
                                filledOnly: !!fieldItem.digitFilledOnly,
                                blankWhenEmpty: !!fieldItem.digitBlankWhenEmpty,
                            }
                            : {currency: presentationCurrency})}
                    />
                ) : isDigitPair ? (
                    <PtrDigitPairValue value={raw}/>
                ) : isYesNo ? (
                    <PtrYesNoRadioGroup value={raw} compact={compact}/>
                ) : isCheckbox ? (
                    <PtrCheckboxValue value={raw}/>
                ) : (
                    display
                )}
            </Box>
        </Box>
    );
}

/**
 * Tax Return preview: nested PTR1-4 tabs, read-only form rows.
 * Labels copied from PTR.xlsx sheet wording; field set = full preview VO.
 */
export function TaxReturnPreviewPanel({
    companyId,
    annualId,
    yearOfAssessment,
    active,
}: TaxReturnPreviewPanelProps) {
    const [sheet, setSheet] = useState<PtrSheet>('PTR1');
    const yoa = (yearOfAssessment || '').trim();
    // Reuse cached annual; no extra preview API. Excel template shows HK$ ? use presentationCurrency.
    const presentationCurrency = useCompanyAnnualStore(
        (s) => s.byAnnualId[annualId]?.presentationCurrency?.trim() || 'HKD',
    );

    const {data, isLoading, isError, error, refetch} = useQuery({
        queryKey: ['fs-preview', 'TAX_RETURN', companyId, annualId, yoa],
        queryFn: () => previewFsReport('TAX_RETURN', companyId, annualId, yoa || undefined),
        enabled: active && companyId > 0 && annualId > 0,
        staleTime: 60_000,
    });

    const payload = data as TaxReturnPreviewPayload | undefined;
    const sheetData = useMemo(() => {
        if (!payload) return null;
        switch (sheet) {
            case 'PTR1':
                return payload.ptr1 ?? null;
            case 'PTR2':
                return payload.ptr2 ?? null;
            case 'PTR3':
                return payload.ptr3 ?? null;
            case 'PTR4':
                return payload.ptr4 ?? null;
            default:
                return null;
        }
    }, [payload, sheet]);

    if (!active) return null;
    if (isLoading) {
        return (
            <Box sx={{p: 3, display: 'flex', justifyContent: 'center', height: '100%'}}>
                <CircularProgress size={28}/>
            </Box>
        );
    }
    if (isError) {
        return (
            <Alert
                severity="error"
                sx={{m: 2}}
                action={<button type="button" onClick={() => void refetch()}>Retry</button>}
            >
                {(error as Error)?.message || 'Failed to load Tax Return preview'}
            </Alert>
        );
    }

    const sections = SHEET_SECTIONS[sheet];
    const compactRows = true;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Tabs
                value={sheet}
                onChange={(_, v: PtrSheet) => setSheet(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    flexShrink: 0,
                    borderBottom: 1,
                    borderColor: 'divider',
                    px: 1,
                    minHeight: 40,
                }}
            >
                {SHEETS.map((s) => (
                    <Tab
                        key={s.key}
                        value={s.key}
                        label={s.label}
                        sx={{minHeight: 40, textTransform: 'none'}}
                    />
                ))}
            </Tabs>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    overflowX: sheet === 'PTR4' ? 'hidden' : 'auto',
                    p: sheet === 'PTR4' ? 1.5 : 2,
                }}
            >
                {!sheetData ? (
                    <Typography color="text.secondary">No {sheet} data.</Typography>
                ) : (
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                        }}
                    >
                        {/* One colgroup for the whole tab ? Part 1/2/3 descriptions share one vertical edge */}
                        <Box component="colgroup">
                            <Box component="col" sx={{width: ITEM_NO_COL_WIDTH}}/>
                            <Box component="col"/>
                            <Box component="col" sx={{width: VALUE_COL_WIDTH}}/>
                        </Box>
                        <Box component="tbody">
                            {sections.map((sectionItem) => (
                                <React.Fragment key={sectionItem.title}>
                                    <Box component="tr">
                                        <Box
                                            component="td"
                                            colSpan={3}
                                            sx={{
                                                pt: 1.5,
                                                pb: 0.75,
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                fontWeight={700}
                                                sx={{whiteSpace: 'pre-wrap'}}
                                            >
                                                {sectionItem.title}
                                                {sectionItem.titleHint ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            color: 'text.secondary',
                                                            fontWeight: 400,
                                                            ml: 1,
                                                        }}
                                                    >
                                                        {sectionItem.titleHint}
                                                    </Box>
                                                ) : null}
                                            </Typography>
                                            {sectionItem.note ? (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    display="block"
                                                    sx={{mt: 0.5, whiteSpace: 'pre-wrap'}}
                                                >
                                                    {sectionItem.note}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                    </Box>
                                    {sectionItem.layout === 'dualColCheckbox' ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={3} sx={{p: 0, pt: 0.5}}>
                                                <Part9DualCheckboxTable
                                                    fields={sectionItem.fields}
                                                    sheetData={sheetData as Record<string, unknown>}
                                                />
                                            </Box>
                                        </Box>
                                    ) : sectionItem.layout === 'part11' ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={3} sx={{p: 0, pt: compactRows ? 0.25 : 0.5}}>
                                                <Part11DualDigitTable
                                                    fields={sectionItem.fields}
                                                    sheetData={sheetData as Record<string, unknown>}
                                                    presentationCurrency={presentationCurrency}
                                                />
                                            </Box>
                                        </Box>
                                    ) : sectionItem.layout === 'dualColDigit' ? (
                                        <>
                                            <Box component="tr">
                                                <Box component="td" colSpan={3} sx={{p: 0, pt: compactRows ? 0.25 : 0.5}}>
                                                    <Part12DualDigitTable
                                                        fields={sectionItem.fields.slice(0, PART12_DIGIT_COUNT)}
                                                        sheetData={sheetData as Record<string, unknown>}
                                                        presentationCurrency={presentationCurrency}
                                                    />
                                                </Box>
                                            </Box>
                                            <Box component="tr">
                                                <Box
                                                    component="td"
                                                    colSpan={3}
                                                    sx={{pt: 1.5, pb: 0.5, borderBottom: 'none'}}
                                                >
                                                    <Typography variant="body2" sx={{mb: 0.75, fontSize: 13}}>
                                                        <Box component="span" sx={{fontWeight: 700}}>
                                                            Director:
                                                        </Box>{' '}
                                                        {inlineTextValue(
                                                            getByPath(sheetData, 'director'),
                                                        )}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{fontSize: 13}}>
                                                        <Box component="span" sx={{fontWeight: 700}}>
                                                            Company&apos;s name:
                                                        </Box>{' '}
                                                        {inlineTextValue(
                                                            getByPath(sheetData, 'companyName'),
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </>
                                    ) : sectionItem.layout === 'descValue4to6' ? (
                                        <Box component="tr">
                                            <Box component="td" colSpan={3} sx={{p: 0, pt: compactRows ? 0.25 : 0.5}}>
                                                <PtrRatioFieldTable
                                                    fields={sectionItem.fields}
                                                    sheetData={sheetData as Record<string, unknown>}
                                                    presentationCurrency={presentationCurrency}
                                                    descPercent={40}
                                                    valuePercent={60}
                                                    compact={compactRows}
                                                />
                                            </Box>
                                        </Box>
                                    ) : sectionItem.layout === 'part6' ? (
                                        <>
                                            <Box component="tr">
                                                <Box component="td" colSpan={3} sx={{p: 0, pt: compactRows ? 0.25 : 0.5}}>
                                                    <PtrRatioFieldTable
                                                        fields={sectionItem.fields.slice(0, 2)}
                                                        sheetData={sheetData as Record<string, unknown>}
                                                        presentationCurrency={presentationCurrency}
                                                        descPercent={20}
                                                        valuePercent={80}
                                                        compact={compactRows}
                                                    />
                                                </Box>
                                            </Box>
                                            {sectionItem.fields.slice(2).map((fieldItem) => (
                                                <PtrFieldRow
                                                    key={fieldItem.path}
                                                    fieldItem={fieldItem}
                                                    sheetData={sheetData as Record<string, unknown>}
                                                    presentationCurrency={presentationCurrency}
                                                    compact={compactRows}
                                                />
                                            ))}
                                        </>
                                    ) : (
                                        sectionItem.fields.map((fieldItem) => (
                                            <PtrFieldRow
                                                key={fieldItem.path}
                                                fieldItem={fieldItem}
                                                sheetData={sheetData as Record<string, unknown>}
                                                presentationCurrency={presentationCurrency}
                                                compact={compactRows}
                                            />
                                        ))
                                    )}
                                </React.Fragment>
                            ))}
                        </Box>
                    </Box>
                )}
                </Box>
        </Box>
    );
}
